import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';
import { LogsSortByOptions } from '../../pages/TravelRequest/TravelReqDetail';

const sapConfigApi = createApi({
  reducerPath: 'sapConfig',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['SapConfigurationFieldSet', 'Contracts', 'SalesOrders'],
  endpoints: builder => ({
    createSapConfigurationField: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/sap-configuration`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ['SapConfigurationFieldSet'],
    }),

    getSapConfigurationField: builder.query<
      BackendResponse<{ count: number; list: any[] }>,
      any
    >({
      query: query => {
        return {
          url: `/sap-configuration${parseQueryParams(query)}`,
          method: 'GET',
        };
      },
    }),

    updateSapConfigurationField: builder.mutation<
      BackendResponse<void>,
      { id: number; [key: string]: any }
    >({
      query: ({ id, ...rest }) => ({
        url: `/sap-configuration/${id}`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ['SapConfigurationFieldSet'],
    }),

    deleteSapConfigurationField: builder.mutation<
      BackendResponse<void>,
      number[] | string[] | number | string
    >({
      query: ids => ({
        url: `/sap-configuration`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ['SapConfigurationFieldSet'],
    }),
    setDefaultSapConfiguration: builder.mutation<
      BackendResponse<void>,
      number[] | string[] | number | string
    >({
      query: id => ({
        url: `/sap-configuration/${id}/set-default`,
        method: 'PATCH',
        body: { id },
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ['SapConfigurationFieldSet'],
    }),

    getActivityLogs: builder.query<
      BackendResponse<{ message: string; data: any[] }>,
      number | string
    >({
      query: id => {
        return {
          url: `/contract/activity-logs/${id}`,
          method: 'GET',
        };
      },
    }),

    getSapLogs: builder.query<
      BackendResponse<any>,
      { id: string; pageSize: number; page: number; sortBy: LogsSortByOptions }
    >({
      query: ({ id, pageSize, page, sortBy }) => ({
        url: `/contract/saplogs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'Contracts', id }],
    }),
    getSalesOrderSapLogs: builder.query<
      BackendResponse<any>,
      { id: number; pageSize: number; page: number; sortBy: LogsSortByOptions }
    >({
      query: ({ id, pageSize, page, sortBy }) => ({
        url: `/sales-order/${id}/sap-logs`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'SalesOrders' as const, id }],
    }),
  }),
});

export const {
  useCreateSapConfigurationFieldMutation,
  useDeleteSapConfigurationFieldMutation,
  useGetSapConfigurationFieldQuery,
  useUpdateSapConfigurationFieldMutation,
  useSetDefaultSapConfigurationMutation,
  useGetActivityLogsQuery,
  useGetSapLogsQuery,
  useGetSalesOrderSapLogsQuery,
} = sapConfigApi;

export default sapConfigApi;
